import {
  Box,
  Grid,
  IconButton,
  InputLabel,
  Switch,
  TableCell,
  tableCellClasses,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ButtonContained from "../../Common/ButtonContained";
import CustomDialog from "../../Common/CustomDialog";
import CommonTextField from "../../Common/CommonTextField";
import TextLabel from "../../Common/CommonTextLabel";
import CommonFileUpload from "../../Common/CommonFileUpload";
import { useTheme } from "@emotion/react";
import { showLoader } from "../../../Redux/Slices/loaderSlice";
import { useDispatch } from "react-redux";
import {
  addBanner,
  create_posts,
  delete_post,
  deleteBanner,
  get_posts,
  getBanner,
  update_post,
  updateBanner,
  uploadImage,
} from "../../../Redux/Actions/action";
import ErrorHandler from "../../../Utils/handler";
import { toast } from "react-toastify";
import CustomPagination from "../../Common/CommonPagination";
import CommonTable from "../../Common/CommonTable";
import { BucketURL } from "../../../ApiSetup/Api";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DataNotFound from "../../Common/DataNotFound";
import Assets from "../../Common/Assets";
import { styled } from "@mui/material/styles";
import CommonDatePicker from "../../Common/CommonDatePicker";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { makeStyles } from "tss-react/mui";
import dayjs from "dayjs";
import ImageIcon from "@mui/icons-material/Image";

const useStyles = makeStyles()((theme) => {
  return {
    timePicker: {
      width: "100%",
      ".MuiInputBase-root:hover fieldset": {
        borderColor: `#c4c4c4 !important`,
      },
      input: {
        padding: "6px 12px 6px 12px !important",
      },
      "& .MuiInputBase-root.MuiOutlinedInput-root": {
        borderRadius: "10px !important",
        flexDirection: "row-reverse",
      },
      "& .MuiInputAdornment-root": {
        margin: "0px 5px 0px 0px",
      },
      ".Mui-focused": {
        borderColor: `red !important`,
        // borderWidth: "1px !important",
      },
      ".MuiSvgIcon-root": {
        color: "#3E4094",
        fontSize: "22px",
      },
      ".MuiInputBase-input": {
        height: "24px",
      },
    },
  };
});

const headerList = [
  "No",
  "Event Image",
  "Event Name",
  "Event Status",
  "Action",
];
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 14,
    // fontWeight: "bold",
    textAlign: "center", // Horizontally align content,
    lineHeight: "1.2rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "0px 10px !important",
    textAlign: "center", // Horizontally align content
    borderBottom: "2px solid #d9d6f3",
  },
}));

function NewsAndEvents() {
  // ---------- Hook -----------
  const theme = useTheme();
  const dispatch = useDispatch();
  const { classes } = useStyles();

  // --------------- State -------------
  const [open, setOpen] = useState(false);
  const [isImagesPopup, setIsImagesPopup] = useState(false);
  const [data, setData] = useState({});
  const [eventList, setEventList] = useState({});
  const [error, setError] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [filePreView, setFilePreView] = useState({
    isOpen: false,
    link: "",
    type: "",
  });
  const [eventImages, setEventImages] = React.useState([]);

  // --------------- handleChange ---------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value.trimStart() });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleClear = () => {
    setOpen(false);
    setIsEdit(false);
    setData({});
    setError({});
  };

  const validation = () => {
    let isValid = true;
    let error = {};
    if (!data.eventName) {
      error.eventName = "Event Name is required";
      isValid = false;
    }
    if (!data.eventImage) {
      error.eventImage = "Event Image is required";
      isValid = false;
    }
    if (!data.eventDescription) {
      error.eventDescription = "Event Description is required";
      isValid = false;
    }
    if (!data.date) {
      error.date = "Event Date is required";
      isValid = false;
    }
    if (!data.time) {
      error.time = "Event Time is required";
      isValid = false;
    }
    setError(error);
    return isValid;
  };

  // --------------- Api ---------------
  const handleImageUpload = async (e, isEventImages) => {
    const { files, name } = e?.target;

    const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    const validTypes = [...validImageTypes];

    const fileData = files[0];
    if (!validTypes.includes(fileData.type)) {
      setError((error) => ({
        ...error,
        eventImageSize:
          "Invalid file type. Only JPEG, PNG and JPG are allowed.",
      }));
      return;
    }

    if (fileData.size > 10485760) {
      setError((error) => ({
        ...error,
        eventImageSize:
          "File size exceeds the 10 MB limit. Please upload a smaller file.",
      }));
      return;
    }

    const formData = new FormData();
    formData.append("image", files[0]);

    dispatch(showLoader(true));
    try {
      const result = await dispatch(uploadImage(formData));
      const error = ErrorHandler(result);

      if (error) {
        const uploadedFile = result?.payload?.data?.[0]?.image;
        if (isEventImages) {
          setEventImages([...eventImages, uploadedFile]);
        } else {
          setData((prevState) => ({
            ...prevState,
            [name]: uploadedFile,
            isImage: validImageTypes.includes(fileData.type),
          }));
        }

        setError((error) => ({
          ...error,
          eventImageSize: "",
          bannerImage: "",
        }));
      }
    } catch (err) {
      console.error("File upload failed:", err);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _createUpdatePosts = async () => {
    if (validation()) {
      dispatch(showLoader(true));
      try {
        let body = {
          type: "News-Events",
          eventDetails: {
            bannerImage: data?.eventImage,
            date: data?.date,
            time: data?.time,
            description: data?.eventDescription,
            eventName: data?.eventName,
            status: data?.eventStatus ? "Active" : "Inactive",
            eventImage: data.images || [],
          },
        };
        if (isEdit && data?._id) {
          body._id = data?._id;
        }
        const fn = isEdit ? update_post : create_posts;
        const result = await dispatch(fn(body));
        const error = ErrorHandler(result);
        if (error) {
          toast.success(result?.payload?.message);
          _get_posts();
          handleClear();
        }
      } catch (error) {
        toast.error(error?.message || "An unknown error occurred");
      } finally {
        dispatch(showLoader(false));
      }
    }
  };

  const _handleSwitch = async (status, details) => {
    dispatch(showLoader(true));
    try {
      let body = {
        type: "News-Events",
        _id: details?._id,
        eventDetails: {
          bannerImage: details?.eventDetails?.bannerImage,
          date: details?.eventDetails?.date,
          time: details?.eventDetails?.time,
          description: details?.eventDetails?.description,
          eventName: details?.eventDetails?.eventName,
          status: status ? "Active" : "Inactive",
          eventImage: [],
        },
      };
      const result = await dispatch(update_post(body));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _get_posts();
      }
    } catch (error) {
      toast.error(error?.message || "An unknown error occurred");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _addEventImages = async () => {
    if (eventImages.length === 0) {
      toast.error("Please select at-least one image");
      return;
    }
    dispatch(showLoader(true));
    try {
      let body = {
        type: "News-Events",
        _id: isImagesPopup?._id,
        eventDetails: {
          bannerImage: isImagesPopup?.eventDetails?.bannerImage,
          date: isImagesPopup?.eventDetails?.date,
          time: isImagesPopup?.eventDetails?.time,
          description: isImagesPopup?.eventDetails?.description,
          eventName: isImagesPopup?.eventDetails?.eventName,
          status: isImagesPopup?.eventDetails?.status ? "Active" : "Inactive",
          eventImage: eventImages,
        },
      };
      const result = await dispatch(update_post(body));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _get_posts();
      }
    } catch (error) {
      toast.error(error?.message || "An unknown error occurred");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _get_posts = async () => {
    dispatch(showLoader(true));
    let body = {
      type: "News-Events",
      page: page,
      limit: rowsPerPage,
    };
    try {
      const result = await dispatch(get_posts(body));
      const error = ErrorHandler(result);
      if (error) {
        setEventList(result?.payload?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _delete_post = async (id) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(delete_post({ _id: id }));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _get_posts();
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  // ---------- useEffect ------------
  useEffect(() => {
    _get_posts();
  }, [page, rowsPerPage]);

  return (
    <>
      <Grid container spacing={1} xs={12}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 500 }}>
              News & Events
            </Typography>
            <ButtonContained text={"Add Event"} onClick={() => setOpen(true)} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <CommonTable headerList={headerList} minWidth={400}>
            {eventList?.data?.length > 0 ? (
              eventList?.data?.map((item, index) => (
                <TableRow key={item.id}>
                  <StyledTableCell align="center">
                    {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {
                      <img
                        src={BucketURL + item?.eventDetails?.bannerImage}
                        height={"40px"}
                        width={"40px"}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const url =
                            BucketURL + item?.eventDetails?.bannerImage;
                          setFilePreView({
                            isOpen: true,
                            link: url,
                            type: "image",
                          });
                        }}
                      />
                    }
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {item?.eventDetails?.eventName}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {" "}
                    <Switch
                      checked={
                        item?.eventDetails?.status === "Active" ? true : false
                      }
                      color="primary"
                      onChange={(e) => {
                        _handleSwitch(e?.target?.checked, item);
                      }}
                    />
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      justifyContent={"center"}
                    >
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => {
                          setIsImagesPopup(item);
                          setEventImages(item?.eventDetails?.eventImage);
                        }}
                      >
                        <ImageIcon fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => {
                          setOpen(true);
                          setIsEdit(true);
                          setData({
                            _id: item?._id,
                            eventName: item.eventDetails?.eventName,
                            eventDescription: item?.eventDetails?.description,
                            eventImage: item?.eventDetails?.bannerImage,
                            date: dayjs(item?.eventDetails?.date),
                            time: dayjs(item?.eventDetails?.time),
                            eventStatus:
                              item?.eventDetails?.status === "Active"
                                ? true
                                : false,
                            images: item?.eventDetails?.eventImage,
                          });
                        }}
                      >
                        <EditIcon fontSize="small" color="success" />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => {
                          _delete_post(item?._id);
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={8}>
                  <DataNotFound
                    title={"No Records Found"}
                    icon={
                      <ErrorOutlineIcon
                        color="primary"
                        style={{ fontSize: "3rem" }}
                      />
                    }
                    elevation={0}
                  />
                </StyledTableCell>
              </TableRow>
            )}
          </CommonTable>
          {eventList?.count > 0 && (
            <Grid item xs={12}>
              <CustomPagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={Math.ceil(eventList?.count / rowsPerPage)}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          )}
        </Grid>
        {open && (
          <CustomDialog
            isOpen={open}
            handleClose={handleClear}
            title={isEdit ? "Update Event" : "Add Event"}
            padding={"10px"}
            width={500}
            action={
              <>
                <ButtonContained
                  text={isEdit ? "Update Event" : "Add Event"}
                  onClick={_createUpdatePosts}
                  minWidth={"100%"}
                  marginTop={"10px"}
                />
              </>
            }
          >
            <Grid container spacing={2} xs={12} ml={-1}>
              <Grid item xs={12}>
                <CommonTextField
                  value={data?.eventName}
                  width={"100%"}
                  text="Event Name"
                  type="text"
                  onChange={(e) => handleInputChange(e)}
                  placeholder="Enter a Event Name"
                  name="eventName"
                  valid
                />
                <TextLabel
                  title={!data?.eventName ? error?.eventName : ""}
                  color="error"
                />
              </Grid>
              <Grid item xs={12}>
                <CommonTextField
                  value={data?.eventDescription}
                  width={"100%"}
                  text="Event Description"
                  type="text"
                  onChange={(e) => handleInputChange(e)}
                  placeholder="Enter a Event Description"
                  name="eventDescription"
                  valid
                  multiline
                  rows={3}
                />
                <TextLabel
                  title={!data?.eventDescription ? error?.eventDescription : ""}
                  color="error"
                />
              </Grid>
              <Grid item xs={6}>
                <CommonDatePicker
                  valid
                  name={"date"}
                  label={"Event Date"}
                  onChange={(date) => setData({ ...data, date: date })}
                  value={data?.date || null}
                />
                <TextLabel
                  title={!data?.date ? error?.date : ""}
                  color="error"
                />
              </Grid>
              <Grid item xs={6}>
                <Box display={"flex"}>
                  <InputLabel
                    sx={{
                      letterSpacing: "0.03em",
                      fontSize: "14px",
                      color: "#222222",
                      whiteSpace: "wrap",
                      marginBottom: "0px",
                      marginRight: "2px",
                    }}
                  >
                    Event Time
                  </InputLabel>
                  <Typography
                    color="#EF627A"
                    component={"caption"}
                    variant={"body2"}
                  >
                    *
                  </Typography>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["TimePicker"]}>
                    <TimePicker
                      // format='hh:mm A'
                      className={classes.timePicker}
                      name={"time"}
                      onChange={(time) => setData({ ...data, time: time })}
                      value={data?.time || null}
                    />
                  </DemoContainer>
                </LocalizationProvider>
                <TextLabel
                  title={!data?.time ? error?.time : ""}
                  color="error"
                />
              </Grid>
              <Grid item xs={12}>
                <CommonFileUpload
                  text={"Upload a Event Image"}
                  padding={{ xs: "5px 14px", sm: "30px 14px", lg: "35px 7px" }}
                  accept="image/jpeg, image/png, image/jpg"
                  labelTitle="image/jpeg, image/png, image/jpg Up to 10MB"
                  name={"eventImage"} // This is the key that will be logged in handleUpload
                  onChange={(e) => handleImageUpload(e)}
                  valid
                />
                {data?.eventImage && (
                  <Assets
                    src={BucketURL + data?.eventImage}
                    height={"50px"}
                    width={"50px"}
                    alt="Banner Image"
                  />
                )}
                <TextLabel
                  title={!data?.eventImage ? error?.eventImage : ""}
                  color="error"
                />
                <TextLabel
                  title={data?.eventImageSize ? "" : error?.eventImageSize}
                  color="error"
                />
              </Grid>
              <Grid item xs={12}>
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <InputLabel
                    sx={{
                      letterSpacing: "0.03em",
                      fontSize: "14px",
                      color: "#222222",
                      whiteSpace: "wrap",
                      marginBottom: "0px",
                    }}
                  >
                    Event Status :
                  </InputLabel>
                  <Switch
                    checked={data?.eventStatus}
                    color="primary"
                    onChange={(e) => {
                      setData({ ...data, eventStatus: e?.target?.checked });
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CustomDialog>
        )}
        {isImagesPopup && (
          <CustomDialog
            isOpen={isImagesPopup}
            handleClose={() => setIsImagesPopup(false)}
            title={"Add Event Images"}
            padding={"10px"}
            width={500}
            action={
              <>
                <ButtonContained
                  text={"Add Event Images"}
                  onClick={_addEventImages}
                  minWidth={"100%"}
                  marginTop={"10px"}
                />
              </>
            }
          >
            <Grid container spacing={2} xs={12} ml={-1}>
              <Grid item xs={12}>
                <CommonFileUpload
                  text={"Upload a Event Images"}
                  padding={{ xs: "5px 14px", sm: "30px 14px", lg: "35px 7px" }}
                  accept="image/jpeg, image/png, image/jpg"
                  labelTitle="image/jpeg, image/png, image/jpg Up to 10MB"
                  name={"eventImage"} // This is the key that will be logged in handleUpload
                  onChange={(e) => handleImageUpload(e, true)}
                  valid
                />
                {eventImages?.length > 0 &&
                  eventImages?.map((item) => {
                    return (
                      <Assets
                        src={BucketURL + item}
                        height={"50px"}
                        width={"50px"}
                        alt="Banner Image"
                      />
                    );
                  })}
                <TextLabel
                  title={!data?.eventImage ? error?.eventImage : ""}
                  color="error"
                />
                <TextLabel
                  title={data?.eventImageSize ? "" : error?.eventImageSize}
                  color="error"
                />
              </Grid>
            </Grid>
          </CustomDialog>
        )}
        {filePreView?.isOpen && (
          <CustomDialog
            isOpen={filePreView?.isOpen}
            handleClose={() =>
              setFilePreView({ isOpen: false, link: "", type: "" })
            }
            title={"View Image"}
            padding={"5px"}
            width={400}
            action={
              filePreView?.type === "image" ? (
                <img
                  src={filePreView?.link}
                  alt="Preview"
                  style={{ width: "100%" }}
                />
              ) : filePreView?.type === "video" ? (
                <video controls style={{ width: "100%" }}>
                  <source src={filePreView?.link} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div>No media available</div>
              )
            }
            maxWidth={"md"}
          ></CustomDialog>
        )}
      </Grid>
    </>
  );
}

export default NewsAndEvents;
